import { useEffect, useState, useRef } from 'react';
import { tap } from '../telegram';

export interface PinPadProps {
  title?: string;
  subtitle?: string;
  error?: string | null;
  onSubmit: (pin: string) => void;
  onCancel: () => void;
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

/**
 * The docked numeric keypad from the chat flow: 1-9, 取消, 0, backspace.
 * Submits automatically once six digits are entered.
 */
export function PinPad({ title = '请输入安全密码', subtitle, error, onSubmit, onCancel }: PinPadProps) {
  const [pin, setPin] = useState('');
  const submitted = useRef(false);
  const submitTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => { if (submitTimer.current) clearTimeout(submitTimer.current); }, []);
  /** The message is dropped as soon as the user starts a new attempt. */
  const [shownError, setShownError] = useState<string | null>(error ?? null);

  useEffect(() => {
    setShownError(error ?? null);
    if (error) { setPin(''); submitted.current = false; }
  }, [error]);

  const push = (digit: string): void => {
    if (submitted.current) return;
    tap();
    setShownError(null);
    const next = `${pin}${digit}`.slice(0, 6);
    setPin(next);
    if (next.length === 6) {
      submitted.current = true;
      submitTimer.current = setTimeout(() => onSubmit(next), 80);
    }
  };

  return (
    <div className="pin-backdrop" role="dialog" aria-modal="true">
      <div className="pin-sheet">
        <h2>{title}</h2>
        <div className="sub">
          {shownError ? (
            <span className="error">{shownError}</span>
          ) : (
            (subtitle ?? '6 位数字安全密码')
          )}
        </div>
        <div className="pin-dots">
          {[0, 1, 2, 3, 4, 5].map((index) => (
            <i key={index} className={index < pin.length ? 'filled' : ''} />
          ))}
        </div>
        <div className="keypad">
          {KEYS.map((key) => (
            <button key={key} type="button" onClick={() => push(key)}>
              {key}
            </button>
          ))}
          <button type="button" className="wide" onClick={onCancel}>
            取消
          </button>
          <button type="button" onClick={() => push('0')}>
            0
          </button>
          <button
            type="button"
            className="wide"
            onClick={() => {
              if (submitted.current) return;
              tap();
              setPin((value) => value.slice(0, -1));
            }}
            aria-label="删除"
          >
            ⌫
          </button>
        </div>
      </div>
    </div>
  );
}
